import { Key } from 'antd/es/table/interface'
import type { BasicDataNode, EventDataNode } from 'antd/es/tree'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { DataNode, PartType } from '../types/types'
import { getActiveTabLabel, getAllKeys } from '../utils'
import { useShallowSelector, useWorkspace2Store } from '../Workspace2Store'
import { useParagraphActions } from './useParagraphActions'

import { useReactiveVar } from '@apollo/client'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { useChatProfileShallowSelector } from 'components/AskDelaChat/ChatProfileStore'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOpenKeyParagraph } from '../components/ContentEditor/contentUtils'
import { useTrackChangesQuery } from '../components/TrackChangesPanel/api/useTrackChangesQuery'
import { useHandleCardPositions } from '../components/TrackChangesPanel/hooks/useHandleCardPositions'
import { useClashDetectionQuery } from '../features/clashDetection/api/useClashDetectionQuery'
import { usePillSelection } from '../features/pillSelection'
import { handleOptionGroupReset } from '../features/pillSelection/utils/pillResetUtils'
import { usePTActivationHandler } from '../features/ptActivation'
import { usePTKeyParagraphValidator } from '../features/ptActivation/hooks/usePTKeyParagraphValidator'
import {
    useHoveredItemActions,
    useShallowHoveredItems,
} from '../HoveredItemStore'
import { useHoveredActionMutationService } from '../services'
import {
    getAllChildrenFromHoveredNode,
    getChildElements,
    getDeactivationTargetElementNodeId,
    getPtNodeDisplayName,
    isParentProductTypeNodeHidden,
} from '../utils/hoveredActionUtils'
import { useEditingPermissions } from './useEditingPermissions'
export type TreeEventDataNode = EventDataNode<BasicDataNode | DataNode>
/** Type of InfoProps for the TreeView component */
export type InfoProps = {
    /** Whether the node is expanded. */
    expanded: boolean
    /** The node to be expanded/collapsed. */
    node: TreeEventDataNode
    /** The native event associated with the node. */
    nativeEvent: MouseEvent
}

/**
 * Custom hook for validating a hyperlink type node
 * @returns {Object} An object containing functions for getting the hyperlink count and checking if a node has a keyed hyperlink
 */
export const useHyperlinkValidator = () => {
    const getHyperlinkCount = useCallback(
        (node: DataNode) =>
            node.contentItemNodes.filter(
                (item) => item.type === NODE_TYPES.Hyperlink
            ).length,
        []
    )

    const hasKeyedHyperlink = useCallback(
        (node: DataNode) =>
            node.contentItemNodes.some((element) => {
                const text = element.text?.trim()
                return (
                    text &&
                    (text === ':' || text.startsWith(':') || text.endsWith(':'))
                )
            }),
        []
    )

    return { getHyperlinkCount, hasKeyedHyperlink }
}

/**
 * Custom hook for handling tree handlers
 * @param {Function} fetchProductFamily - Function to fetch product family data
 * @param {Function} fetchBestPracticeElementData - Function to fetch best practice element data
 * @param {Function} fetchProjectData - Function to fetch project data
 * @returns {Object} An object containing functions for handling tree events
 */
export const useTreeHandlers = (
    fetchProductFamily?: (partType: PartType, ids?: string[]) => void,
    fetchBestPracticeElementData?: (elementId: string) => void,
    fetchProjectData?: () => void
) => {
    const {
        isContentTeamFromStudioPage,
        contentItemViewNodes,
        articlesLoading,
        expandedKeys,
        activeTab,
        setExpandedKeys,
        setArticlesLoading,
        setIsEditingPlainText,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
        contentItemViewNodes: state.contentItemViewNodes,
        articlesLoading: state.articlesLoading,
        expandedKeys: state.expandedKeys,
        activeTab: state.activeTab,
        setExpandedKeys: state.actions.setExpandedKeys,
        setArticlesLoading: state.actions.setArticlesLoading,
        setIsEditingPlainText: state.actions.setIsEditingPlainText,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    const { addFirstParagraphToArticle } = useParagraphActions()

    // Guards newly-created articles: only articles that explicitly went through
    // handleExpandCollapseArticle get added here, preventing injection while the
    // API-created paragraph is still in-flight.
    const pendingEphemeralIds = useRef<Set<string>>(new Set())
    const injectedEphemeralIds = useRef<Set<string>>(new Set())

    const { nugget } = useContext(AreaContext)

    const { mutateDeactivationAsync } = useHoveredActionMutationService()
    const { fetchClashDetection } = useClashDetectionQuery()

    const { projectId, projectData } = useContext(ProjectPageContext)
    const role = useChatProfileShallowSelector((state) => state.chatUser?.role)
    const {
        checkAndHandlePTActivationIfNeeded,
        shouldWarnPTDeactivation,
        checkAndHandlePTDeactivation,
    } = usePTActivationHandler()

    // PT key paragraph validation - used directly for Reviewer filtering
    const { isPTKeyParagraphWithInactivePT } = usePTKeyParagraphValidator()

    const {
        isMainMenuDisplayed,
        selectedMainMenuNode,
        isBPMenuDisplayed,
        isManageOptionsDisplayed,
        isNotesMenuDisplayed,
        isMoveBlockMenuDisplayed,
        manageOptionSelected,
        showClashDetectionDialog,
        showSustainabilityWarningDialog,
    } = useShallowHoveredItems((state) => ({
        isMainMenuDisplayed: state.isMainMenuDisplayed,
        selectedMainMenuNode: state.selectedMainMenuNode,
        isBPMenuDisplayed: state.isBPMenuDisplayed,
        isManageOptionsDisplayed: state.isManageOptionsDisplayed,
        isNotesMenuDisplayed: state.isNotesMenuDisplayed,
        isMoveBlockMenuDisplayed: state.isMoveBlockMenuDisplayed,
        manageOptionSelected: state.manageOptionSelected,
        showClashDetectionDialog: state.showClashDetectionDialog,
        showSustainabilityWarningDialog: state.showSustainabilityWarningDialog,
    }))

    const {
        setIsMainMenuDisplayed,
        setIsBPMenuDisplayed,
        setIsManageOptionsDisplayed,
        setManageOptionSelected,
        setIsNotesMenuDisplayed,
        setHoveredNode,
        setSelectedMainMenuNode,
        setIsProductTypeDeactivationDialogOpen,
        setIsMoveBlockMenuDisplayed,
        setDragAnchorElement,
        setClashDetectionResponse,
        setShowClashDetectionDialog,
        setShowSustainabilityWarningDialog,
        setSustainabilityParameters,
        setPtDeactivationDialogInfo,
    } = useHoveredItemActions()

    const { activateDeactivateOptionItem } = usePillSelection()
    const { fetchTrackChangesData } = useTrackChangesQuery()

    const [menuElement, setMenuElement] = useState<HTMLButtonElement | null>(
        null
    )
    const handleCardPositions = useHandleCardPositions()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )

    const { canEdit } = useEditingPermissions()

    // Computed value for dialog states to simplify checks
    const isAnyDialogOpen =
        showClashDetectionDialog || showSustainabilityWarningDialog

    const handleMouseHover = useCallback(
        (hoveredNodeEvent) => {
            // Block hover updates when dialogs are open - backdrop prevents interaction anyway
            if (isAnyDialogOpen) return

            setHoveredNode(
                !!hoveredNodeEvent?.node
                    ? hoveredNodeEvent?.node
                    : hoveredNodeEvent
            )
        },
        [setHoveredNode, isAnyDialogOpen]
    )
    const handleMouseLeave = useCallback(() => {
        // Block clearing hover when main menu or dialogs are displayed
        if (isMainMenuDisplayed || isAnyDialogOpen) return

        setHoveredNode(undefined)
    }, [setHoveredNode, isMainMenuDisplayed, isAnyDialogOpen])
    /**
     * Opens main context menu when drag icon is clicked.
     * Disabled when family/product type not in project.
     */
    const handleDragIconClick = useCallback(
        (e: React.MouseEvent, node: DataNode) => {
            if (!canEdit) {
                return
            }

            if (!selectedProjectElementNode?.isInProject) {
                return
            }

            // Disable plain text editing when opening menu
            setIsEditingPlainText(false)

            // Find the corresponding content item node
            const selectedNode = contentItemViewNodes?.find(
                (item) => item?.id === node?.key
            )

            if (!selectedNode) {
                console.warn(`Selected node not found for key: ${node?.key}`)
                return
            }

            // Toggle the main menu display state
            setIsMainMenuDisplayed(!isMainMenuDisplayed)

            // NOTES MENU BEHAVIOR FIX:
            // Reset Notes Menu state to ensure main menu shows when opening.
            // This prevents the Notes submenu from appearing after user clicked outside
            // during a previous Notes menu session and then reopened the menu.
            if (!isMainMenuDisplayed) {
                setIsNotesMenuDisplayed(false)
            }

            // Set the anchor element for popover positioning
            if (e.currentTarget) {
                setDragAnchorElement(e.currentTarget)
            }

            // Update state with the selected node and hovered node
            setSelectedMainMenuNode(selectedNode)
            setHoveredNode(node)
        },
        [
            canEdit,
            contentItemViewNodes,
            setDragAnchorElement,
            setIsMainMenuDisplayed,
            setSelectedMainMenuNode,
            setHoveredNode,
            isMainMenuDisplayed,
            setIsEditingPlainText,
            setIsNotesMenuDisplayed,
        ]
    )
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const onCloseMainMenu = useCallback(() => {
        setIsMainMenuDisplayed(false)
        setHoveredNode(undefined)
        setSelectedMainMenuNode(undefined)
        setDragAnchorElement(null)

        // NOTES MENU BEHAVIOR FIX:
        // Reset Notes Menu state when closing main menu to ensure clean state
        // for the next time the menu is opened
        setIsNotesMenuDisplayed(false)
    }, [
        setIsMainMenuDisplayed,
        setSelectedMainMenuNode,
        setHoveredNode,
        setDragAnchorElement,
        setIsNotesMenuDisplayed,
    ])

    const onClickBPMenu = useCallback(
        (e) => {
            setIsBPMenuDisplayed(!isBPMenuDisplayed)
            if (selectedMainMenuNode?.id) {
                fetchBestPracticeElementData?.(selectedMainMenuNode.id)
            }
            fetchProjectData?.()
            if (e.currentTarget) {
                setMenuElement(e.currentTarget)
            }
        },
        [
            selectedMainMenuNode,
            isBPMenuDisplayed,
            fetchBestPracticeElementData,
            fetchProjectData,
        ]
    )
    const onClickManageOptions = useCallback(
        (e) => {
            setIsManageOptionsDisplayed(!isManageOptionsDisplayed)
            setManageOptionSelected(null)
            if (e.currentTarget) {
                setMenuElement(e.currentTarget)
            }
        },
        [
            isManageOptionsDisplayed,
            setManageOptionSelected,
            setIsManageOptionsDisplayed,
            setMenuElement,
        ]
    )
    const onClickNotesMenu = useCallback(() => {
        setIsNotesMenuDisplayed(!isNotesMenuDisplayed)
    }, [isNotesMenuDisplayed, setIsNotesMenuDisplayed])
    /**
     * Closes Manage Options dialog and main menu.
     */
    const onCancelManageOptions = useCallback(() => {
        setIsManageOptionsDisplayed(false)
        onCloseMainMenu()
    }, [setIsManageOptionsDisplayed, onCloseMainMenu])
    // Type guard to validate TextContentElement array
    const isTextContentElementArray = (
        arr: any[]
    ): arr is TextContentElement[] => {
        return arr.every((el) => el && typeof el === 'object' && 'id' in el)
    }

    /**
     * Saves Manage Options selections and closes dialogs.
     */
    const onSaveManageOptions = useCallback(() => {
        // Always close dialogs first
        setIsManageOptionsDisplayed(false)
        onCloseMainMenu()

        // Early validation checks
        if (
            !manageOptionSelected ||
            !selectedMainMenuNode?.id ||
            !selectedMainMenuNode?.elements
        ) {
            return
        }

        // Validate elements array structure
        const elements = selectedMainMenuNode.elements
        if (!Array.isArray(elements)) {
            console.warn('Selected main menu node elements is not an array')
            return
        }

        if (!isTextContentElementArray(elements)) {
            console.error(
                'Invalid elements structure - not TextContentElement array'
            )
            return
        }

        try {
            // Apply the selected option (reset/deactivate) to all elements
            const updatedElements = handleOptionGroupReset(
                elements,
                manageOptionSelected
            )

            const updatedMainMenuNode = {
                ...selectedMainMenuNode,
                elements: updatedElements,
            } as TextContentElement

            // Execute the activation/deactivation mutation
            activateDeactivateOptionItem(
                updatedMainMenuNode,
                selectedMainMenuNode.id,
                true
            )
        } catch (error) {
            console.error('Error saving manage options:', error)
        }
    }, [
        setIsManageOptionsDisplayed,
        onCloseMainMenu,
        manageOptionSelected,
        selectedMainMenuNode,
        activateDeactivateOptionItem,
    ])

    /**
     * Determines whether the product type deactivation should be disabled for a given node.
     */
    const disableProductTypeInactivation = useCallback(
        (node: Partial<DataNode>) => {
            if (isContentTeamFromStudioPage && !isCurrentlyOnOMWorkspace)
                return true

            //  If the selected element type is 'PRODUCTTYPE', disables inactivation for article or product type content.
            const isRestrictedProductTypeActivation =
                node?.type === 'Article' || node?.isProductType

            // For selected product family    disables inactivation for nodes that are either product types or product groups.
            const isRestrictedProductTypeContentOnFamilyView =
                node.isProductType || node.isProductGroup

            return selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
                ? isRestrictedProductTypeActivation
                : isRestrictedProductTypeContentOnFamilyView ||
                      isParentProductTypeNodeHidden(node, contentItemViewNodes)
        },
        [
            isContentTeamFromStudioPage,
            selectedProjectElementNode,
            contentItemViewNodes,
            isCurrentlyOnOMWorkspace,
        ]
    )

    const handleElementDeactivation = useCallback(
        async (isHidden: boolean, hoveredNode: DataNode) => {
            // Store the node key to find it later with fresh state
            const hoveredNodeKey = hoveredNode.key

            // This ensures we have the most recent state even if component hasn't re-rendered
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const selectedContentItemNode = latestNodes?.find(
                (item) => item?.id === hoveredNodeKey
            )

            if (!selectedContentItemNode) {
                return
            }

            // Capture deactivation status BEFORE optimistic update clears it
            // Check both hoveredNode (state at click time) and store (latest state)
            const wasDeactivated =
                hoveredNode?.isDeactivated ||
                selectedContentItemNode?.isDeactivated ||
                false

            const isHiddenInOMWorkspace = isCurrentlyOnOMWorkspace
                ? false
                : isHidden

            // Always clear UI state first (even if PT dialog shown)
            setHoveredNode(undefined)
            setShowClashDetectionDialog(false)
            setClashDetectionResponse(null)

            // Check for inactive PTs and handle activation dialog if needed
            if (
                await checkAndHandlePTActivationIfNeeded(
                    selectedContentItemNode,
                    latestNodes,
                    selectedProjectElementNode ?? undefined,
                    isHidden,
                    () => {
                        handleElementDeactivation(isHidden, hoveredNode)
                    }
                )
            ) {
                return
            }

            let pendingNodeKey: string | undefined
            try {
                // workaround to apply optimistic update
                let updatedNodes = getAllChildrenFromHoveredNode(
                    selectedContentItemNode,
                    latestNodes,
                    isHidden
                )

                // For Reviewers activating content: keep inactive PT key paragraphs hidden
                // PT paragraphs linked to inactive PTs (not added to project) should stay hidden
                // PT paragraphs linked to active PTs (already in project) can be activated
                if (role === 'Reviewer' && !isHidden && updatedNodes) {
                    // Collect IDs of all inactive PT key paragraphs and their descendants
                    const idsToKeepHidden = new Set<string>()
                    const visited = new Set<string>()

                    const collectDescendants = (
                        node: Partial<typeof latestNodes[0]>
                    ) => {
                        getChildElements(node, latestNodes).forEach((child) => {
                            if (!child.id || visited.has(child.id)) return
                            visited.add(child.id)
                            idsToKeepHidden.add(child.id)
                            collectDescendants(child)
                        })
                    }

                    latestNodes.forEach((node) => {
                        if (isPTKeyParagraphWithInactivePT(node) && node.id) {
                            idsToKeepHidden.add(node.id)
                            collectDescendants(node)
                        }
                    })

                    updatedNodes = updatedNodes.map((node) => {
                        if (node.id && idsToKeepHidden.has(node.id)) {
                            return { ...node, isHidden: true }
                        }
                        return node
                    })
                }

                if (updatedNodes) {
                    setContentItemViewNodes(updatedNodes)
                }

                const nodeId:
                    | string
                    | undefined = getDeactivationTargetElementNodeId(
                    selectedProjectElementNode,
                    useWorkspace2Store.getState().familyAddedProductTypes,
                    hoveredNode
                )

                const parentParagraph = latestNodes?.find((item) =>
                    selectedContentItemNode?.dtvContentItemParentId
                        ? item.id ===
                          selectedContentItemNode.dtvContentItemParentId
                        : item.dtvItemId ===
                          selectedContentItemNode?.dtvParentId
                )
                const input = {
                    documentTemplateViewId:
                        selectedContentItemNode?.documentTemplateViewId,
                    isHidden: isHiddenInOMWorkspace,
                    contentItemOwnerId: selectedContentItemNode?.dtvItemId,
                    projectId,
                    nodeId,
                    contentItemId: selectedContentItemNode?.id,
                    projectNuggetId: nugget?.id,
                    // OM Workspace: No track changes for activation/deactivation
                    // Check if viewing from Studio/Element Library (not project workspace)
                    isTrackChangeToggleOn: !isCurrentlyOnOMWorkspace,
                    // OM Workspace or hovered node was deactivated from OM/SMM workspace: toggle deactivation state (true to deactivate, false to re-activate)
                    ...((isCurrentlyOnOMWorkspace || wasDeactivated) && {
                        isContentDeactivatedInOM: !wasDeactivated,
                    }),
                    isParentOpenKey: isOpenKeyParagraph(
                        parentParagraph?.elements ?? []
                    ),
                }

                pendingNodeKey = hoveredNodeKey as string
                useWorkspace2Store
                    .getState()
                    .actions.addDeactivationPending(pendingNodeKey)
                await mutateDeactivationAsync(input)
                await fetchTrackChangesData()
            } catch (error) {
                console.error('Failed to mutate deactivation:', error)
                // On error, revert to original state
                setContentItemViewNodes(latestNodes)
            } finally {
                if (pendingNodeKey) {
                    useWorkspace2Store
                        .getState()
                        .actions.removeDeactivationPending(pendingNodeKey)
                }
            }
        },
        [
            role,
            isPTKeyParagraphWithInactivePT,
            mutateDeactivationAsync,
            fetchTrackChangesData,
            setHoveredNode,
            setShowClashDetectionDialog,
            setClashDetectionResponse,
            projectId,
            selectedProjectElementNode,
            nugget,
            setContentItemViewNodes,
            checkAndHandlePTActivationIfNeeded,
            isCurrentlyOnOMWorkspace,
        ]
    )

    /**
     * Handles element activation/deactivation with PT checks
     * - For activation: Checks if PT activation warning is needed
     * - For deactivation: Checks if PT deactivation warning is needed
     * If no warning needed, proceeds with normal activation/deactivation.
     */
    const handleElementDeactivationWithPTCheck = useCallback(
        async (isHidden: boolean, hoveredNode: DataNode) => {
            // Get the latest content item node
            const hoveredNodeKey = hoveredNode.key
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const selectedContentItemNode = latestNodes?.find(
                (item) => item?.id === hoveredNodeKey
            )

            // Skip PT checks if feature is disabled or required nodes are missing
            if (!selectedContentItemNode || !selectedProjectElementNode) {
                await handleElementDeactivation(isHidden, hoveredNode)
                return
            }

            const removePending = () =>
                useWorkspace2Store
                    .getState()
                    .actions.removeDeactivationPending(
                        hoveredNode.key as string
                    )

            // Case 1: Activating content (isHidden = false)
            if (
                await checkAndHandlePTActivationIfNeeded(
                    selectedContentItemNode,
                    latestNodes,
                    selectedProjectElementNode ?? undefined,
                    isHidden,
                    () => {
                        handleElementDeactivation(isHidden, hoveredNode)
                    }
                )
            ) {
                removePending()
                return
            }

            // Case 2: Deactivating content (isHidden = true)
            if (isHidden) {
                if (
                    shouldWarnPTDeactivation(
                        selectedContentItemNode,
                        selectedProjectElementNode
                    )
                ) {
                    removePending()
                    checkAndHandlePTDeactivation(
                        selectedContentItemNode,
                        selectedProjectElementNode
                    )
                    return
                }
            }

            // No PT warning needed - proceed with normal activation/deactivation
            await handleElementDeactivation(isHidden, hoveredNode)
        },
        [
            checkAndHandlePTActivationIfNeeded,
            shouldWarnPTDeactivation,
            checkAndHandlePTDeactivation,
            selectedProjectElementNode,
            handleElementDeactivation,
        ]
    )

    /**
     * Handles the activation/deactivation (hiding/unhiding) of a content item node.
     * Applies an optimistic UI update and sends a mutation request to the backend.
     *
     * @param isHidden - Whether the content item should be hidden.
     * @param hoveredNode - The currently interacted node.
     */
    const handleShowHideProperty = useCallback(
        async (
            isHidden: boolean,
            hoveredNode: DataNode,
            disablePTAutomation: boolean
        ) => {
            // Show spinner immediately — before any async checks.
            const {
                addDeactivationPending,
                removeDeactivationPending,
            } = useWorkspace2Store.getState().actions
            const removePending = () =>
                removeDeactivationPending(hoveredNode.key as string)
            addDeactivationPending(hoveredNode.key as string)

            if (!selectedProjectElementNode?.isInProject) {
                removePending()
                return
            }

            if (hoveredNode.isProductGroup || hoveredNode.isProductType) {
                removePending()
                const storeNodes = useWorkspace2Store.getState().contentItemViewNodes
                const nodeName = getPtNodeDisplayName(hoveredNode, storeNodes)
                setPtDeactivationDialogInfo({
                    isProductGroup: hoveredNode.isProductGroup ?? false,
                    isArticleLevel: hoveredNode.type === NODE_TYPES.Article,
                    isHidden,
                    nodeName,
                })
                setIsProductTypeDeactivationDialogOpen(true)
                return
            }

            if (disableProductTypeInactivation(hoveredNode)) {
                removePending()
                setPtDeactivationDialogInfo(null)
                setIsProductTypeDeactivationDialogOpen(true)
                return
            }

            // Store the node key to find it later with fresh state
            const hoveredNodeKey = hoveredNode.key

            // This ensures we have the most recent state even if component hasn't re-rendered
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes

            const selectedContentItemNode = latestNodes?.find(
                (item) => item?.id === hoveredNodeKey
            )

            if (!selectedContentItemNode) {
                removePending()
                return
            }

            // Show warning when:
            // 1. Activating hidden non-selected sustainability content, OR
            // 2. Deactivating visible selected sustainability content
            const isActivatingHiddenNonSelected =
                !selectedContentItemNode?.isSelectedSustainabilityContentItem &&
                selectedContentItemNode?.isHidden

            const isDeactivatingVisibleSelected =
                selectedContentItemNode?.isSelectedSustainabilityContentItem &&
                !selectedContentItemNode?.isHidden

            if (
                selectedContentItemNode?.isSustainabilityContentItem &&
                (isActivatingHiddenNonSelected || isDeactivatingVisibleSelected)
            ) {
                // Extract sustainability parameters from project data
                const sustainabilityArray = projectData?.getProject?.parameters?.find(
                    (parameter) =>
                        parameter?.modifierList?.name === 'Sustainability Type'
                )
                const sustainabilityParams =
                    sustainabilityArray?.selectedModifiers
                        ?.map((modifier) => modifier.name)
                        .join(', ') || ''

                removePending()
                setSustainabilityParameters(sustainabilityParams)
                setShowSustainabilityWarningDialog(true)
                setHoveredNode(hoveredNode)
                return
            }

            if (disablePTAutomation) {
                await handleElementDeactivationWithPTCheck(
                    isHidden,
                    hoveredNode
                )
                return
            }
            try {
                const isArticle =
                    selectedContentItemNode?.textType === 'Article'
                const clashDetectionResponse = await fetchClashDetection({
                    projectId: projectId ?? '',
                    documentTemplateViewId:
                        selectedContentItemNode.documentTemplateViewId ?? '',
                    documentTemplateViewItemId:
                        selectedContentItemNode.dtvItemId ?? '',
                    isArticle,
                    documentTemplateViewItemHiddenStatus: isHidden,
                })

                // Check if there are clashed product types
                // If there are clashes, the mutation and track changes should still proceed
                // The clash detection is informational but doesn't block the operation
                if (
                    clashDetectionResponse?.clashDetection?.clashedProductTypes
                        ?.length
                ) {
                    removePending()
                    setClashDetectionResponse(clashDetectionResponse)
                    setShowClashDetectionDialog(true)
                    setHoveredNode(hoveredNode)
                } else {
                    setClashDetectionResponse(null)
                    await handleElementDeactivationWithPTCheck(
                        isHidden,
                        hoveredNode
                    )
                }
            } catch (error) {
                console.error('Failed to fetch clash detection:', error)
                // On error, proceed with the operation anyway to not block the user
                // Show error notification so user is aware
                await handleElementDeactivationWithPTCheck(
                    isHidden,
                    hoveredNode
                )
            }
        },
        [
            selectedProjectElementNode,
            nugget,
            setHoveredNode,
            setContentItemViewNodes,
            projectId,
            fetchClashDetection,
            handleElementDeactivation,
            mutateDeactivationAsync,
            fetchTrackChangesData,
            disableProductTypeInactivation,
            setIsProductTypeDeactivationDialogOpen,
            setPtDeactivationDialogInfo,
            setClashDetectionResponse,
            setShowClashDetectionDialog,
            handleElementDeactivationWithPTCheck,
        ]
    )

    const handleExpand = useCallback(
        (expandedKeys: Key[], { expanded, node, nativeEvent }: InfoProps) => {
            const level = node.pos.split('-').length

            if (level !== 2) {
                nativeEvent.preventDefault()
                nativeEvent.stopPropagation()
                return
            }

            const keySet = new Set(expandedKeys)
            const descendantKeys = new Set(
                getAllKeys((node as DataNode).children || [])
            )

            if (expanded) {
                descendantKeys.add(node.key.toString())
                descendantKeys.forEach((key) => keySet.add(key))
            } else {
                descendantKeys.add(node.key.toString())
                descendantKeys.forEach((key) => keySet.delete(key))
            }
            setExpandedKeys(Array.from(keySet))
            handleCardPositions()
        },
        [setExpandedKeys, handleCardPositions]
    )

    // #2716145: injects an ephemeral first-paragraph into expanded articles that
    // came back empty from the fetch. pendingEphemeralIds gates this to articles
    // that explicitly went through handleExpandCollapseArticle, so it is a no-op
    // on unrelated contentItemViewNodes re-renders.
    useEffect(() => {
        const {
            expandedKeys: currentExpanded = [],
        } = useWorkspace2Store.getState()
        const expandedSet = new Set(currentExpanded)

        const emptyArticles = contentItemViewNodes.filter(
            (node) =>
                node.textType === 'Article' &&
                node.id &&
                expandedSet.has(node.id) &&
                pendingEphemeralIds.current.has(node.id) &&
                !injectedEphemeralIds.current.has(node.id) &&
                // Mirrors TreeDataMapper.findChildren: user-added children link via
                // dtvContentItemParentId, DTV children via dtvParentId, BOD children
                // via refBodItemId. All three arms prevent false-empty detection.
                !contentItemViewNodes.some(
                    (n) =>
                        n.dtvContentItemParentId === node.id ||
                        (node.dtvItemId &&
                            n.dtvParentId === node.dtvItemId &&
                            !n.dtvContentItemParentId) ||
                        (node.dtvItemId && n.refBodItemId === node.dtvItemId)
                )
        )

        if (emptyArticles.length === 0) return

        const emptyIds = new Set(emptyArticles.map((n) => n.id!))
        const currentLoading =
            useWorkspace2Store.getState().articlesLoading ?? []
        if (currentLoading.some((id) => emptyIds.has(id))) {
            setArticlesLoading(currentLoading.filter((id) => !emptyIds.has(id)))
        }

        for (const article of emptyArticles) {
            pendingEphemeralIds.current.delete(article.id!)
            injectedEphemeralIds.current.add(article.id!)
            addFirstParagraphToArticle({ key: article.id } as DataNode)
        }
    }, [contentItemViewNodes, addFirstParagraphToArticle, setArticlesLoading])

    const handleLoadData = useCallback(
        ({ key }: TreeEventDataNode) => {
            if (!expandedKeys.includes(key)) {
                setArticlesLoading([...articlesLoading, key.toString()])

                if (fetchProductFamily) {
                    fetchProductFamily(getActiveTabLabel(activeTab), [
                        key.toString(),
                    ])
                }
            }

            return Promise.resolve()
        },
        [
            activeTab,
            expandedKeys,
            articlesLoading,
            setArticlesLoading,
            fetchProductFamily,
        ]
    )

    const handleExpandCollapseArticle = useCallback(
        (expandedKeys: Key[], shouldExpand: boolean, node: DataNode) => {
            if (!node?.key) return

            const articleId = node.key
            const keySet = new Set(expandedKeys)
            const descendantKeys = new Set(
                getAllKeys((node as DataNode).children || [])
            )

            descendantKeys.add(articleId)
            descendantKeys.forEach((key) =>
                shouldExpand ? keySet.add(key) : keySet.delete(key)
            )

            if (shouldExpand) {
                if (node?.children?.length === 0) {
                    const id = articleId.toString()
                    const currentLoading = useWorkspace2Store.getState()
                        .articlesLoading
                    // Skip injection for articles already in loading (pre-loaded by navigation),
                    // unless the article was just created by the user (genuinely empty).
                    if (!currentLoading.includes(id) || node.isUserAdded) {
                        injectedEphemeralIds.current.delete(id)
                        pendingEphemeralIds.current.add(id)
                    }
                    if (!currentLoading.includes(id)) {
                        setArticlesLoading([...currentLoading, id])
                    }
                    fetchProductFamily?.(getActiveTabLabel(activeTab), [id])
                }
            } else {
                const id = articleId.toString()
                // If an ephemeral blank paragraph was injected into this article,
                // collapsing means the user abandoned it — remove the unsaved node
                // and release the editing lock so the workspace stays interactive.
                const hadEphemeral = injectedEphemeralIds.current.has(id)
                pendingEphemeralIds.current.delete(id)
                injectedEphemeralIds.current.delete(id)
                if (hadEphemeral) {
                    const {
                        contentItemViewNodes: latestNodes,
                        textContentElementToAdd: addingElement,
                        actions,
                    } = useWorkspace2Store.getState()
                    if (addingElement) {
                        const updated = latestNodes.filter(
                            (n) => n.elements?.[0]?.id !== addingElement.id
                        )
                        if (updated.length < latestNodes.length) {
                            actions.setContentItemViewNodes(updated)
                        }
                        actions.setTextContentElementToAdd(null)
                    }
                }
                const currentLoading =
                    useWorkspace2Store.getState().articlesLoading ?? []
                if (currentLoading.includes(id)) {
                    setArticlesLoading(
                        currentLoading.filter((loadingId) => loadingId !== id)
                    )
                }
            }

            setExpandedKeys(Array.from(keySet))

            handleCardPositions()
        },
        [
            setExpandedKeys,
            handleCardPositions,
            activeTab,
            fetchProductFamily,
            setArticlesLoading,
        ]
    )

    /**
     * Toggles the visibility of the Move Block menu and sets the anchor element for the menu.
     * @param {MouseEvent<HTMLButtonElement>} e - The click event triggered by the user.
     */
    const onClickMoveBlockMenu = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            setIsMoveBlockMenuDisplayed(!isMoveBlockMenuDisplayed)
            if (e.currentTarget) {
                setMenuElement(e.currentTarget)
            }
        },
        [isMoveBlockMenuDisplayed, setIsMoveBlockMenuDisplayed, setMenuElement]
    )

    /**
     * Prevents the default action when pressing the Enter key on a tree view node.
     * @param {React.KeyboardEvent} e - The keyboard event.
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
        }
    }, [])

    const handleScroll = useCallback(async () => {
        try {
            await handleCardPositions()
        } catch (error) {
            console.error('Error handling card positions on scroll:', error)
        }
    }, [handleCardPositions])

    return {
        menuElement,
        disableProductTypeInactivation,
        handleExpand,
        handleExpandCollapseArticle,
        handleLoadData,
        handleDragIconClick,
        onCloseMainMenu,
        onClickBPMenu,
        onClickManageOptions,
        onClickNotesMenu,
        onCancelManageOptions,
        onSaveManageOptions,
        handleShowHideProperty,
        handleElementDeactivation,
        onClickMoveBlockMenu,
        handleKeyDown,
        handleMouseHover,
        handleMouseLeave,
        handleScroll,
    }
}
